<template>
  <v-app :style="{ background: $vuetify.theme.themes.light.background }">
    <v-main>
      <v-container class="mb-16 pt-10 pt-md-15">
        <div class="d-md-flex justify-md-space-between">
          <div class="text-h4">
            {{ this.isCharibon ? "お申込み" : "買取お申込み" }}
          </div>
          <entry-steps current-step="STEP3"/>
        </div>
        <v-sheet :outlined="$vuetify.breakpoint.mdAndUp" max-width="840px" class="mx-auto white">
          <v-row class="mt-4 px-md-10 px-2">
            <v-col>
              <p style="word-wrap: break-word;">{{ getMemberFormItems.email }}へ認証コードをお送りしました。</p>
              <p class="font-weight-bold">
                認証コードは
                <span class="error--text font-weight-bold">{{
                    getMemberLabelItems.authenticationExpirationDatetimeFormatted
                  }}まで有効</span>となっております。<br>
                この画面は消さずに、メールに記載された６桁の認証コードをご入力ください。
              </p>

            </v-col>
          </v-row>

          <v-row class="px-md-10 px-2">
            <v-col id="otp-input" cols="12">
              <div class="ma-auto mb-md-4" style="width: 300px">
                <v-otp-input
                    length="6"
                    type="number"
                    :disabled="loading"
                    @finish="moveTemporaryToPersistence"
                />
                <v-overlay absolute :value="loading">
                  <v-progress-circular indeterminate color="primary"/>
                </v-overlay>
              </div>
            </v-col>
          </v-row>

          <v-row class="px-md-10 px-2" v-if="$vuetify.breakpoint.mdAndUp">
            <v-divider style="border-top: 1px solid #ccc"/>
          </v-row>

          <v-row class="px-md-10 px-2">
            <v-col cols="12" class="pt-12 pb-6">
              <p class="font-weight-bold">メールが届かない場合は以下の設定をご確認ください。</p>
              <p class="mb-0">
                【スマートフォン、携帯電話の場合】<br/>ドメイン指定受信
                {{ domainReceptionAnnotationText }}
                の設定<br/><br/>
                【パソコンの場合】<br/>迷惑メールボックスの設定の解除<br/><br/>
                上記内容が設定されている場合には、ご入力のメールアドレスにお間違えがあった可能性がありますので、
                今一度入力の内容をお確かめのうえ、再送信をお願いいたします。
              </p>
            </v-col>
          </v-row>

          <v-row class="pb-8 px-md-3 px-2">
            <v-btn
                class="mx-md-3 text-decoration-underline"
                :class="{ 'secondary--text': !isCharibon }"
                :color="isCharibon ? '#1a73e8' : null"
                text
                @click="refreshOtpThenNotify"
            >
              認証コードを再送信する
            </v-btn>
          </v-row>
        </v-sheet>
      </v-container>
    </v-main>

    <v-dialog v-model="dialogAuthenticationIdentityHasRefreshed" width="300">
      <v-card>
        <v-card-title class="text-h6 my-4 justify-center">
          認証コードを再送信しました
        </v-card-title>

        <v-card-text>
          <v-row>メールを確認頂き、6桁の認証コードを入力してください。</v-row>
        </v-card-text>

        <v-card-actions class="my-4">
          <v-spacer></v-spacer>
          <v-btn @click="dialogAuthenticationIdentityHasRefreshed = false">閉じる</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogRetryableError" width="300">
      <v-card>
        <v-card-title class="text-h6 my-4 justify-center">
          エラーが発生しました
        </v-card-title>

        <v-card-text>
          <v-row>エラーを検出したため、実行することができませんでした。</v-row>
          <v-row>しばらく時間をおいてからもう一度アクセスして頂くか、管理者にお問い合わせください。</v-row>
        </v-card-text>

        <v-card-actions class="my-4">
          <v-spacer></v-spacer>
          <v-btn @click="dialogRetryableError = false">閉じる</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogAuthenticationIdentityDoesNotMatched" width="300">
      <v-card>
        <v-card-title class="text-h6">
          認証コードが間違っています
        </v-card-title>
        <v-card-text>
          <v-row>認証コードが間違っております。メールをご確認の上、再度ご入力ください。</v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="dialogAuthenticationIdentityDoesNotMatched = false">閉じる</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogAuthenticationIdentityIsExpired" width="300" :persistent="true">
      <v-card>
        <v-card-title class="text-h6">
          認証コードの有効期限切れです
        </v-card-title>
        <v-card-text>
          <v-row>
            認証コードの有効期限内に認証が完了しなかったため、セキュリティを確保するために、認証コードが無効になりました。
          </v-row>
          <v-row>恐れ入りますが、再度お申込みください。</v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn class="mx-auto" @click="goToEntryForm">お申込フォームへ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogAuthenticationIdentityUnableToRefresh" width="300" :persistent="true">
      <v-card>
        <v-card-title class="text-h6">
          認証コードを再発行できません
        </v-card-title>
        <v-card-text>
          <v-row>リクエストが不正、または認証コードの有効期限を超過したため、あらたな認証コードを再発行できませんでした。
          </v-row>
          <v-row>恐れ入りますが、再度お申込みください。</v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn class="mx-auto" @click="goToEntryForm">お申込フォームへ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogPickupTimeDeadlineExceeded" width="300" :persistent="true">
      <v-card>
        <v-card-title class="text-h6">
          集荷の受付可能日時を過ぎています
        </v-card-title>
        <v-card-text>
          <v-row>ご指定の集荷日時は、すでに受付可能な日時を過ぎています。</v-row>
          <v-row>恐れ入りますが、再度お申込みください。</v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn class="mx-auto" @click="goToEntryForm">お申込フォームへ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogShouldBeAbortedError.occurred" width="300" :persistent="true">
      <v-card>
        <v-card-title class="text-h6">
          エラーが発生しました
        </v-card-title>
        <v-card-text>
          <v-row>{{ dialogShouldBeAbortedError.cause }}</v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn class="mx-auto" href="/">トップページへ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import GtmOnVue from "../../../libs/gtmOnVue.js";
import EntrySteps from "../../molecules/buy/entry-steps.vue"

// ブラウザバックの抑制
if (window.history && window.history.pushState) {
  if (document.getElementById('otp-input')) {
    //. ブラウザ履歴に１つ追加
    history.pushState("no history back", null, "");

    $(window).on("popstate", function (event) {
      //. このページで「戻る」を実行
      if (!event.originalEvent.state) {
        //. もう一度履歴を操作して終了
        history.pushState("no history back", null, "");
        return;
      }
    });
  }
}

export default {
  name: "quick-entry-otp",
  components: {
    EntrySteps,
  },
  props: {
    formPathName: {type: String, required: true},
    completePathName: {type: String, required: true}
  },
  beforeRouteEnter(to, from, next) {
    // Back to the form page, if this confirmation page is reloaded.
    if (!from.name) {
      console.info('pickup date is empty on authentication');

      next({name: this.formPathName});
      return;
    }
    next()
  },
  data: () => ({
    loading: false,
    dialogAuthenticationIdentityHasRefreshed: false,
    dialogRetryableError: false,
    dialogAuthenticationIdentityDoesNotMatched: false,
    dialogAuthenticationIdentityIsExpired: false,
    dialogAuthenticationIdentityUnableToRefresh: false,
    dialogPickupTimeDeadlineExceeded: false,
    dialogShouldBeAbortedError: {
      occurred: false,
      cause: null,
    },
    suppressConfirmDialogOnLeave: false,
    domainReceptionAnnotationItems: {
      [VueConfig.siteCode.valuebooks]: '（@valuebooks.jp および @value-books.jp）',
      [VueConfig.siteCode.vaboo]: '（@valuebooks.jp および @value-books.jp）',
      [VueConfig.siteCode.charibon]: '（@value-books.jp）',
    }
  }),
  created() {
    window.addEventListener('beforeunload', this.confirmDialogOnLeave, false);

    if (this.isCharibon) {
      GtmOnVue.sendGAEventCharibonOfferAll(
          this.getBuyFormItems.supportGroupId,
          this.getBuyFormItems.campaignCode,
          this.getBuyFormItems.vbOfficeCode
      );
    }
  },
  destroyed() {
    window.removeEventListener('beforeunload', this.confirmDialogOnLeave, false);
  },
  methods: {
    ...mapActions({
      updateFormItems: 'buyState/updateFormItems',
      updateMemberFormLabels: 'member/updateFormLabels',
      updateBuyFormItems: 'buy/updateFormItems',
    }),
    moveTemporaryToPersistence(otp) {
      if (this.loading) {
        return;
      }
      this.loading = true;

      axios.post('/api/member/move-temporary-to-persistence', {
        UUID: this.getMemberLabelItems.requestUUID,
        one_time_code: otp,

      }).then(response => {
        this.loading = false;

        if (response.status === 200) {
          this.suppressConfirmDialogOnLeave = true;

          // siteコードによって遷移処理を分岐
          switch (this.$siteCode) {
            case VueConfig.siteCode.valuebooks:
              this.updateMemberFormLabels({
                name: 'isMemberRegisterGrantPoint',
                value: response.data.isMemberRegisterGrantPoint
              });
              this.updateFormItems({name: 'buyNumber', value: response.data.buyID});
              this.updateBuyFormItems({name: 'autoAppliedCouponCode', value: response.data.autoAppliedCouponCode});
              this.$router.push({name: this.completePathName});
              break
            case VueConfig.siteCode.vaboo:
              this.updateFormItems({name: 'buyNumber', value: response.data.buyID});
              if (this.getMemberFormItems.isAgreedCreateMainSiteAccount) {
                this.updateMemberFormLabels({
                  name: 'isMemberRegisterGrantPoint',
                  value: response.data.isMemberRegisterGrantPoint
                });
              }
              this.$router.push({name: this.completePathName});  // vabooRouter.jsで定義
              break
            case VueConfig.siteCode.charibon:
              this.updateFormItems({name: 'buyNumber', value: response.data.buyID});
              this.$router.push({name: this.completePathName});
              break
            default:
              //FIXME エラーにすべきところ
              this.$router.push({name: this.completePathName});
              break
          }
          return;
        }
        this.dialogRetryableError = true;

      }).catch(e => {
        this.loading = false;

        if (e.response.status === 401) {
          if (e.response.data.cause === 'OTP is expired.') {
            this.dialogAuthenticationIdentityIsExpired = true;
            return;
          }
          this.dialogAuthenticationIdentityDoesNotMatched = true;
          return;
        }
        if (e.response.status === 422) {
          if (e.response.data.cause === 'Failed to request the pickup.') {
            this.dialogPickupTimeDeadlineExceeded = true;
            return;
          }
        }
        if (e.response.status === 409) {
          this.dialogShouldBeAbortedError.occurred = true;
          this.dialogShouldBeAbortedError.cause = e.response.data.message;
          return;
        }

        this.dialogRetryableError = true;
      });
    },
    refreshOtpThenNotify() {
      axios.post('/api/ajax/member/authentication-identity/refresh', {
        UUID: this.getMemberLabelItems.requestUUID,

      }).then(() => {
        this.dialogAuthenticationIdentityHasRefreshed = true;

      }).catch(e => {
        if (e.response.status === 404) {
          this.dialogAuthenticationIdentityUnableToRefresh = true;
          return;
        }

        if (e.response.status === 401 && e.response.data.cause === 'OTP is expired.') {
          this.dialogAuthenticationIdentityIsExpired = true;
          return;
        }

        this.dialogRetryableError = true;
      });
    },
    goToEntryForm() {
      this.$router.push({name: this.formPathName});
    },
    confirmDialogOnLeave(event) {
      if (this.suppressConfirmDialogOnLeave) {
        return;
      }

      if (document.getElementById('otp-input')) {
        event.preventDefault();
        event.returnValue = 'Check';
      }
    },
  },
  computed: {
    ...mapGetters({
      getMemberFormItems: 'member/getFormItems',
      getMemberLabelItems: 'member/getFormLabels',
      getBuyFormItems: 'buy/getFormItems',
      getRequireIdentityUploading: 'buy/getRequireIdentityUploading',
    }),
    isCharibon() {
      return this.$siteCode === VueConfig.siteCode.charibon;
    },
    isAgreedCreateMainSiteAccountBySiteCode() {
      if (this.$siteCode === VueConfig.siteCode.valuebooks) {
        return true;
      }
      return this.getMemberFormItems.isAgreedCreateMainSiteAccount
    },
    domainReceptionAnnotationText() {
      return this.domainReceptionAnnotationItems[this.$siteCode];
    }
  }
}
</script>

<style scoped lang="scss">
.position-relative {
  position: relative;
}

::v-deep {
  .v-application--wrap {
    min-height: unset;
  }

  #otp-input {
    input {
      background-color: initial;
      border: initial;
    }
  }
}
</style>